import { createAsyncThunk } from '@reduxjs/toolkit';
import { serializeError } from 'shared/utils/redux';
import { PerspectivesListModel } from 'api/dto/new/Perspectives/PerspectiveListDto';
import { PerspectiveModel } from 'api/dto/new/Perspectives/Perspective.dto';
import { RequestPerspectiveDto } from 'api/dto/new/Perspectives/RequestPerspective.dto';
import { perspectivesApi } from 'container/perspectivesApi';
import { CustomAvatarModel } from 'api/dto/new/Perspectives/CustomAvatar.dto';
import { aiAvatarServiceApi } from 'container/aiAvatarServiceApi';
import { PerspectiveSummaryModel } from 'api/dto/new/Perspectives/PerspectiveSummary.dto';
import { aiSummaryServiceApi } from 'container/aiSummaryServiceApi';
import {
  DepartmentsListResultModel,
  PerspectivesDepartmentModel,
} from 'api/dto/new/Perspectives/DepartmentsListResult.dto';
import { COMMENT_CONFIDENTIALITY_THRESHOLD } from 'shared/constants/confidentiality';

import { STORE_NAME } from './initialState';

export const fetchPerspectivesList = createAsyncThunk<PerspectivesListModel, { surveyId: string }>(
  `${STORE_NAME}/fetchPerspectivesList`,
  ({ surveyId }) => perspectivesApi.list(surveyId),
  { serializeError }
);

export const fetchPerspective = createAsyncThunk<
  PerspectiveModel,
  { surveyId: string; perspectiveId: string }
>(
  `${STORE_NAME}/fetchPerspective`,
  ({ surveyId, perspectiveId }) => perspectivesApi.get(surveyId, perspectiveId),
  { serializeError }
);

export const fetchPerspectiveTopics = createAsyncThunk<string[]>(
  `${STORE_NAME}/fetchPerspectiveTopics`,
  () => perspectivesApi.getTopics(),
  { serializeError }
);

const checkDepartmentsAvailability = (departments: DepartmentsListResultModel): boolean => {
  return departments.items.some((dept) => {
    const meetsThreshold =
      dept.arePrerequisitesMet && dept.commentResponses >= COMMENT_CONFIDENTIALITY_THRESHOLD;

    if (meetsThreshold) {
      return true;
    }

    return checkDepartmentsAvailability(dept.children);
  });
};

export const fetchPerspectiveDepartments = createAsyncThunk<
  PerspectivesDepartmentModel,
  { surveyId: string }
>(
  `${STORE_NAME}/fetchPerspectiveDepartments`,
  async ({ surveyId }) => {
    const departmentsList = await perspectivesApi.getDepartments(surveyId);
    const isDepartmentsAvailable = checkDepartmentsAvailability(departmentsList);
    return { departmentsList, isDepartmentsAvailable };
  },
  { serializeError }
);

export const fetchCustomAvatars = createAsyncThunk<CustomAvatarModel[], { clientId: string }>(
  `${STORE_NAME}/fetchCustomAvatars`,
  ({ clientId }) =>
    aiAvatarServiceApi.getCustomAvatars({
      clientId,
    }),
  { serializeError }
);

export const requestPerspective = createAsyncThunk<
  string,
  { surveyId: string; requestPerspectiveDto: RequestPerspectiveDto }
>(
  `${STORE_NAME}/requestPerspective`,
  ({ surveyId, requestPerspectiveDto }) => perspectivesApi.request(surveyId, requestPerspectiveDto),
  { serializeError }
);

export const fetchPerspectiveSummary = createAsyncThunk<
  PerspectiveSummaryModel,
  { perspectiveId: string }
>(
  `${STORE_NAME}/fetchPerspectiveSummary`,
  ({ perspectiveId }) => aiSummaryServiceApi.getPerspectiveSummary({ perspectiveId }),
  { serializeError }
);
